<!-- Guide Drawer Backdrop -->
<div 
  class="guide-drawer-backdrop" 
  [class.active]="isOpen" 
  (click)="close()">
</div>

<!-- Guide Drawer Right Panel -->
<aside 
  class="guide-drawer-panel" 
  [class.open]="isOpen" 
  aria-label="Certiplate Product Guide">

  <!-- Drawer Header -->
  <div class="drawer-header">
    <div class="drawer-header-main">
      <div class="drawer-brand-row">
        <div class="drawer-brand-icon">
          <i class="fas fa-compass"></i>
        </div>
        <div class="drawer-title-block">
          <span class="drawer-kicker">INTERACTIVE TOUR & HELP</span>
          <h2 class="drawer-title">Guide Center</h2>
        </div>
      </div>
      <button 
        type="button" 
        class="drawer-close-btn" 
        (click)="close()" 
        title="Close Guide">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- Overall Tour Progress Bar -->
    <div class="drawer-overall-progress">
      <div class="progress-info-row">
        <span class="progress-label">Overall Completion</span>
        <span class="progress-stats">
          <b>{{ overallProgress.completed }}</b> / {{ overallProgress.total }} steps ({{ overallProgress.percentage }}%)
        </span>
      </div>
      <div class="progress-track">
        <div class="progress-fill" [style.width.%]="overallProgress.percentage"></div>
      </div>
    </div>
  </div>

  <!-- Search Filter (Shown in Modules View) -->
  <div class="drawer-search-bar" *ngIf="!selectedModule">
    <div class="search-input-wrapper">
      <i class="fas fa-search search-icon"></i>
      <input 
        type="text" 
        [(ngModel)]="searchQuery" 
        placeholder="Search modules, features, or steps..." 
        class="search-input" />
      <button 
        type="button" 
        class="search-clear-btn" 
        *ngIf="searchQuery" 
        (click)="searchQuery = ''">
        <i class="fas fa-times-circle"></i>
      </button>
    </div>
  </div>

  <!-- =========================================================================
       VIEW 1: FEATURE / MODULE SEGREGATION LIST
       ========================================================================= -->
  <div class="drawer-body" *ngIf="!selectedModule">
    <div class="section-heading-row">
      <h3 class="section-title">Explore Modules</h3>
      <span class="section-subtitle">Select a feature below to view its guided tour steps</span>
    </div>

    <div class="module-cards-stack">
      <div 
        *ngFor="let module of filteredModules" 
        class="module-card" 
        (click)="selectModule(module)"
        [class.completed]="getModuleStatus(module) === 'completed'"
        [class.in-progress]="getModuleStatus(module) === 'in_progress'">
        
        <div class="module-card-top">
          <div class="module-icon-badge">
            <i class="fas fa-{{ module.icon }}"></i>
          </div>

          <div class="module-text-block">
            <div class="module-title-row">
              <h4 class="module-title">{{ module.title }}</h4>
              <span class="module-tag-badge">{{ module.tag }}</span>
            </div>
            <p class="module-desc">{{ module.description }}</p>
          </div>

          <div class="module-arrow">
            <i class="fas fa-chevron-right"></i>
          </div>
        </div>

        <!-- Module Progress Footer -->
        <div class="module-card-footer">
          <div class="module-steps-count">
            <i class="far fa-check-circle" *ngIf="getModuleStatus(module) === 'completed'"></i>
            <span>{{ module.steps.length }} steps</span>
          </div>

          <!-- Status Badge -->
          <div class="module-status-badge" [ngClass]="getModuleStatus(module)">
            <span *ngIf="getModuleStatus(module) === 'completed'">Completed</span>
            <span *ngIf="getModuleStatus(module) === 'in_progress'">
              {{ getModuleProgress(module.id).completed }}/{{ getModuleProgress(module.id).total }} Done
            </span>
            <span *ngIf="getModuleStatus(module) === 'not_started'">Not Started</span>
          </div>
        </div>

        <!-- Mini Progress Line -->
        <div class="module-mini-progress">
          <div class="module-mini-progress-fill" [style.width.%]="getModuleProgress(module.id).percentage"></div>
        </div>
      </div>

      <!-- Empty State -->
      <div class="empty-search-state" *ngIf="filteredModules.length === 0">
        <i class="fas fa-search empty-icon"></i>
        <p class="empty-text">No guide modules found matching "{{ searchQuery }}"</p>
        <button type="button" class="btn-clear-search" (click)="searchQuery = ''">Clear Search</button>
      </div>
    </div>
  </div>

  <!-- =========================================================================
       VIEW 2: INDIVIDUAL MODULE TOUR STEPS
       ========================================================================= -->
  <div class="drawer-body" *ngIf="selectedModule">
    <!-- Back Navigation Bar -->
    <div class="module-detail-nav">
      <button type="button" class="back-nav-btn" (click)="backToModules()">
        <i class="fas fa-arrow-left"></i>
        <span>All Modules</span>
      </button>
      <span class="module-tag-badge detail-tag">{{ selectedModule.tag }}</span>
    </div>

    <!-- Active Module Hero Banner -->
    <div class="module-hero-banner">
      <div class="hero-top">
        <div class="hero-icon-badge">
          <i class="fas fa-{{ selectedModule.icon }}"></i>
        </div>
        <div class="hero-text-block">
          <h3 class="hero-title">{{ selectedModule.title }}</h3>
          <p class="hero-desc">{{ selectedModule.description }}</p>
        </div>
      </div>

      <div class="hero-progress-block">
        <div class="hero-progress-labels">
          <span class="hero-progress-label">Module Progress</span>
          <span class="hero-progress-counter">
            {{ getModuleProgress(selectedModule.id).completed }} of {{ selectedModule.steps.length }} Completed
          </span>
        </div>
        <div class="progress-track hero-track">
          <div class="progress-fill hero-fill" [style.width.%]="getModuleProgress(selectedModule.id).percentage"></div>
        </div>
      </div>

      <!-- Play Full Module Walkthrough CTA -->
      <button 
        type="button" 
        class="btn-play-module-tour" 
        (click)="launchFullModuleTour(selectedModule)">
        <i class="fas fa-play-circle"></i>
        <span>Play Complete Module Tour</span>
      </button>
    </div>

    <!-- Steps List Header -->
    <div class="steps-section-header">
      <h4 class="steps-section-title">Tour Steps</h4>
      <span class="steps-hint">Click any step to navigate and start the walkthrough</span>
    </div>

    <!-- Steps Stack -->
    <div class="steps-stack">
      <div 
        *ngFor="let step of selectedModule.steps; let i = index" 
        class="step-item-card" 
        (click)="launchStep(step)"
        [class.step-completed]="isStepCompleted(step.id)">
        
        <!-- Step Number / Status Icon -->
        <div class="step-num-indicator" [class.done]="isStepCompleted(step.id)">
          <i class="fas fa-check" *ngIf="isStepCompleted(step.id)"></i>
          <span *ngIf="!isStepCompleted(step.id)">{{ i + 1 }}</span>
        </div>

        <!-- Step Content Block -->
        <div class="step-info-block">
          <div class="step-title-row">
            <h5 class="step-title">{{ step.title }}</h5>
            <span class="step-badge" *ngIf="step.badge">{{ step.badge }}</span>
          </div>
          <p class="step-desc">{{ step.description }}</p>
          <div class="step-meta-row">
            <span class="step-route-pill" *ngIf="step.route">
              <i class="fas fa-link"></i> {{ step.route }}
            </span>
          </div>
        </div>

        <!-- Launch Step Action -->
        <div class="step-action-trigger">
          <button 
            type="button" 
            class="btn-launch-step" 
            [title]="'Start step: ' + step.title">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Drawer Footer Actions -->
  <div class="drawer-footer">
    <div class="footer-left">
      <button 
        type="button" 
        class="btn-reset-progress" 
        (click)="resetProgress()" 
        title="Reset Tour Progress">
        <i class="fas fa-redo-alt"></i> Reset Progress
      </button>
    </div>
    <div class="footer-right">
      <span class="footer-hint">Certiplate Guided Assistance</span>
    </div>
  </div>
</aside>
